import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { DataContext } from "../../context/DataContext";
import { getToken } from "../../api/Account";
import { simpleMessage } from "../../helpers/Helpers";

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

/**
 * Pide un dato del panel y lleva el estado de carga y de error de SU tarjeta.
 *
 * Conserva lo que hacían los widgets: una llamada a la API al montar, el overlay global
 * (`setIsLoading`) y "Unauthorized", que abre la pantalla de no autorizado. Añade el estado
 * local para que cada tarjeta muestre su esqueleto o su error (`EstadoError` con "Reintentar").
 * El Swal solo queda para "Sesión activa en otro dispositivo" (eX01): el resto de fallos los
 * explica la propia tarjeta.
 *
 * @param {(token: string) => Promise<{statusResponse: boolean, data: any, error: any}>} obtener
 *   Función de `api/Dashboard` que se llama (la misma de siempre).
 * @param {(data: any) => void} alRecibir Recibe `result.data` cuando la respuesta fue exitosa.
 * @returns {{ cargando: boolean, error: false|true|"conexion", recargar: () => Promise<void> }}
 *   `error` es "conexion" cuando la llamada lanzó una excepción (sin red: `errorResponse` falla
 *   porque no hay `error.response`).
 */
export const useDatoPanel = (obtener, alRecibir) => {
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  // Siempre la versión más reciente de las funciones, sin volver a pedir el dato por ello.
  const obtenerRef = useRef(obtener);
  const alRecibirRef = useRef(alRecibir);
  obtenerRef.current = obtener;
  alRecibirRef.current = alRecibir;

  const recargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await obtenerRef.current(getToken());
      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        setError(true);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION || result.error === "eX01") {
          simpleMessage(result.error, "error");
        }
        return;
      }

      setIsLoading(false);
      try {
        alRecibirRef.current(result.data);
      } catch (e) {
        // Una respuesta con una forma inesperada no debe dejar la tarjeta en blanco.
        setError(true);
      }
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  }, [setIsLoading, setIsUnautorized]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  return { cargando, error, recargar };
};
